import { useState, useEffect, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';

export default function ContactPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitStatus('submitting');

    try {
      const form = e.target as HTMLFormElement;
      const formDataToSubmit = new URLSearchParams();
      
      formDataToSubmit.append('name', formData.name);
      formDataToSubmit.append('email', formData.email);
      formDataToSubmit.append('phone', formData.phone);
      formDataToSubmit.append('company', formData.company);
      formDataToSubmit.append('subject', formData.subject);
      formDataToSubmit.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5npj7of4392hm13o6fg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSubmit.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: '',
        });
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('idle'), 5000);
      }
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  const contactInfo = [
    {
      icon: 'ri-mail-line',
      title: 'Email Us',
      detail: 'sam@horizonpathwaysllc.com',
      link: 'mailto:sam@horizonpathwaysllc.com',
    },
    {
      icon: 'ri-phone-line',
      title: 'Call Us',
      detail: '+91 99854 48446',
      link: 'tel:+919985448446',
    },
    {
      icon: 'ri-time-line',
      title: 'Business Hours',
      detail: 'Mon - Fri: 9:00 AM - 6:00 PM',
      link: '#',
    },
  ];

  return (
    <div className="relative bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-white'
        }`}
      >
        <div className="px-4 sm:px-6 md:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-3">
              <img
                src="https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/b857441a8282eff6e67d45b8b6cd5de0.jpeg"
                alt="Horizon Pathways Logo"
                className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 object-contain"
              />
              <span className="text-base sm:text-lg md:text-xl font-semibold text-gray-900">
                Horizon Pathways
              </span>
            </Link>

            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              <Link
                to="/"
                className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap"
              >
                Home
              </Link>
              <Link
                to="/products"
                className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap"
              >
                Products
              </Link>
              <Link
                to="/services"
                className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap"
              >
                Services
              </Link>
              <Link
                to="/about"
                className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap"
              >
                About
              </Link>
              <Link
                to="/contact"
                className="text-sm font-medium text-emerald-600 transition-colors hover:text-emerald-700 whitespace-nowrap"
              >
                Contact
              </Link>
            </div>

            <div className="flex items-center gap-3 sm:gap-4">
              <Link
                to="/contact"
                className="px-4 sm:px-5 md:px-6 py-1.5 sm:py-2 border-2 border-emerald-600 text-emerald-600 rounded-full text-xs sm:text-sm font-medium transition-all hover:bg-emerald-600 hover:text-white whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      )}

      {/* Mobile Menu */}
      <div
        className={`fixed top-0 right-0 h-full w-64 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out md:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
            <span className="text-lg font-semibold text-gray-900">Menu</span>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-8 h-8 flex items-center justify-center text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer"
              aria-label="Close menu"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          {/* Mobile Menu Links */}
          <div className="flex-1 overflow-y-auto py-6">
            <div className="flex flex-col space-y-1 px-4">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 text-base font-medium text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 rounded-lg transition-colors cursor-pointer"
              >
                Home
              </Link>
              <Link
                to="/products"
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 text-base font-medium text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 rounded-lg transition-colors cursor-pointer"
              >
                Products
              </Link>
              <Link
                to="/services"
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 text-base font-medium text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 rounded-lg transition-colors cursor-pointer"
              >
                Services
              </Link>
              <Link
                to="/about"
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 text-base font-medium text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 rounded-lg transition-colors cursor-pointer"
              >
                About
              </Link>
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 text-base font-medium text-emerald-600 bg-emerald-50 rounded-lg cursor-pointer"
              >
                Contact
              </Link>
            </div>

            {/* Mobile Menu CTA */}
            <div className="px-4 mt-6">
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full px-6 py-3 bg-emerald-600 text-white text-center rounded-full text-base font-medium hover:bg-emerald-700 transition-all whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative pt-24 sm:pt-28 md:pt-32 pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6 md:px-8 bg-gradient-to-br from-emerald-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            Get in Touch With Us
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-4">
            Have questions about our products or services? We're here to help. Reach out to our team and we'll get back to you as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-12 sm:mb-16 md:mb-20">
            {contactInfo.map((info, index) => (
              <a
                key={index}
                href={info.link}
                className="bg-white border-2 border-gray-200 p-6 sm:p-8 rounded-xl sm:rounded-2xl hover:border-emerald-600 hover:shadow-lg transition-all cursor-pointer text-center"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <i className={`${info.icon} text-emerald-600 text-xl sm:text-2xl`}></i>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                  {info.title}
                </h3>
                <p className="text-xs sm:text-sm text-gray-600">{info.detail}</p>
              </a>
            ))}
          </div>

          {/* Contact Form and Additional Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12">
            {/* Form */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
                Send Us a Message
              </h2>
              <form
                id="contact-page-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-4 sm:space-y-5 md:space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      required
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:border-emerald-600 focus:outline-none transition-colors text-sm"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      required
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:border-emerald-600 focus:outline-none transition-colors text-sm"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:border-emerald-600 focus:outline-none transition-colors text-sm"
                      placeholder="+91 98765 43210"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={(e) =>
                        setFormData({ ...formData, company: e.target.value })
                      }
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:border-emerald-600 focus:outline-none transition-colors text-sm"
                      placeholder="Your Company"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={(e) =>
                      setFormData({ ...formData, subject: e.target.value })
                    }
                    required
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:border-emerald-600 focus:outline-none transition-colors text-sm"
                    placeholder="What can we help you with?"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setFormData({ ...formData, message: e.target.value });
                      }
                    }}
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:border-emerald-600 focus:outline-none transition-colors text-sm resize-none"
                    placeholder="Tell us more about your requirements..."
                  ></textarea>
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {formData.message.length}/500 characters
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitStatus === 'submitting'}
                  className="w-full px-6 sm:px-8 py-3 sm:py-3.5 md:py-4 bg-emerald-600 text-white rounded-full text-sm sm:text-base font-medium transition-all hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {submitStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg text-center">
                    <i className="ri-check-line text-green-600 text-2xl mb-2"></i>
                    <p className="text-xs sm:text-sm text-green-700 font-medium">
                      Thank you! Your message has been sent successfully. We'll get back to you soon.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg text-center">
                    <i className="ri-error-warning-line text-red-600 text-2xl mb-2"></i>
                    <p className="text-xs sm:text-sm text-red-700 font-medium">
                      Oops! Something went wrong. Please try again later.
                    </p>
                  </div>
                )}
              </form>
            </div>

            {/* Why Choose Us */}
            <div>
              <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 p-6 sm:p-8 rounded-xl sm:rounded-2xl text-white">
                <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">
                  Why Choose Horizon Pathways?
                </h3>
                <ul className="space-y-2 sm:space-y-3">
                  {[
                    'Premium quality agricultural products',
                    'Global export expertise',
                    'Complete documentation support',
                    'Certified and compliant operations',
                    'Timely delivery worldwide',
                    'Dedicated customer support',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-emerald-200 text-base sm:text-lg mt-0.5 sm:mt-1 flex-shrink-0"></i>
                      <span className="text-xs sm:text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
